/**
 * Reine Logik für die Wochen-Angaben der Ferienbetreuung (#978).
 *
 * Entstanden für den Ferienbetreuungs-Export der employee-ui: der wies bisher
 * nur die Anzahl der Wochen und blanke Datumsbereiche aus. Hier entsteht die
 * fortlaufende Ferienwochen-Nummer samt Beschriftung, aus der sowohl die
 * Sammelzelle als auch eine Spalte je Woche gebaut wird.
 *
 * Liegt seit #1017 in `libs/utils` statt in der employee-ui, weil die
 * customer-ui dieselbe Normalisierung und dieselbe Datumsformatierung braucht,
 * um Eltern ihre gebuchten Ferienwochen anzuzeigen. Eine dritte Kopie wollte
 * niemand pflegen; die Kopie in den Cloud Functions bleibt notwendig (die
 * können die Nx-Libraries nicht importieren) und wird vom Parity-Test
 * `apps/parity-tests/holiday-care-export.parity.spec.ts` abgeglichen.
 *
 * Bewusst ohne Firestore-`Timestamp` und ohne `DatePipe`: die Funktionen
 * rechnen auf Epoch-Millis und bleiben so ohne TestBed prüfbar.
 */

import { timestampToMillis } from './date-formatter';

/** Eine Ferienwoche, reduziert auf das, was der Export braucht. */
export interface ExportWeek {
  startMillis: number;
  endMillis: number;
}

/**
 * Eine Woche, wie sie tatsächlich in Firestore liegt.
 *
 * In `selectedWeeks` stehen zwei Feldnamen-Varianten nebeneinander: die
 * reguläre Anmeldung über das Elternportal legt `startDate`/`endDate` an,
 * die Admin-Umbuchung (#956) dagegen `start`/`end`. Die Ferienkonfiguration
 * (`availableWeeks`) nutzt ebenfalls `start`/`end`. Gelesen werden deshalb
 * beide Varianten — sonst bleibt der Export für alle nicht umgebuchten
 * Anmeldungen leer (#981).
 */
export interface RawVacationWeek {
  weekNumber?: number;
  start?: unknown;
  end?: unknown;
  startDate?: unknown;
  endDate?: unknown;
}

/** Rohe Woche auf Epoch-Millis bringen, unabhängig von der Feldnamen-Variante. */
export function toExportWeek(
  raw: RawVacationWeek | null | undefined
): ExportWeek {
  return {
    startMillis: timestampToMillis(raw?.start ?? raw?.startDate),
    endMillis: timestampToMillis(raw?.end ?? raw?.endDate),
  };
}

/** `selectedWeeks` einer Anmeldung, wie der Export sie als Rohwert liefert. */
export function toExportWeeks(value: unknown): ExportWeek[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((week) => toExportWeek(week as RawVacationWeek));
}

/** Eine Ferienwoche mit fortlaufender Nummer und fertiger Beschriftung. */
export interface NumberedVacationWeek extends ExportWeek {
  /** Fortlaufende Ferienwoche innerhalb der Ferienperiode, 1-basiert. */
  number: number;
  /** Beschriftung für den Export, mit Jahr: `Woche 1 (03.08.–07.08.2026)`. */
  label: string;
  /** Kurzform für die Tabelle, ohne Jahr: `Woche 1 (03.08.–07.08.)`. */
  shortLabel: string;
}

/**
 * Eine Woche ist nur mit beiden Zeitpunkten verwertbar. `0` gilt als fehlend —
 * `timestampToMillis` liefert das für leere Felder, und der 01.01.1970 ist in
 * einer Ferienkonfiguration immer ein Datenfehler.
 */
function isValidWeek(week: ExportWeek | null | undefined): week is ExportWeek {
  return (
    !!week &&
    Number.isFinite(week.startMillis) &&
    Number.isFinite(week.endMillis) &&
    week.startMillis > 0 &&
    week.endMillis > 0
  );
}

/**
 * Ferienwochen sind Kalendertage, keine Zeitpunkte — gerechnet und formatiert
 * wird deshalb ausdrücklich in Europe/Berlin und nicht in der Zeitzone der
 * Laufzeitumgebung (#997).
 *
 * Die Wochen liegen als Firestore-Timestamps und damit als absolute Zeitpunkte
 * in der Datenbank, und zwar in zwei Ausprägungen nebeneinander: die
 * migrierten `availableWeeks` (#991) tragen die deutsche Mitternacht
 * (`2026-10-11T22:00Z`), ältere Buchungen die UTC-Mitternacht desselben Tages
 * (`2026-10-12T00:00Z`). In Europe/Berlin ist beides der 12.10. In der
 * UTC-Zeitzone der Cloud Functions dagegen der 11. bzw. der 12.10. — die
 * Wochen wurden dort einen Tag zu früh beschriftet und die Buchung fiel bei
 * der tagesweisen Zuordnung aus ihrer Woche heraus. Im Export der
 * Herbstferien 2026 erschien dadurch eine dritte Woche, die es nicht gibt.
 */
const BERLIN_DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Kalendertag in Europe/Berlin als `2026-10-05`.
 *
 * Ersetzt den früheren `startOfDay`: Zeitpunkte am selben deutschen
 * Kalendertag ergeben denselben Schlüssel, unabhängig von der Uhrzeit und von
 * der Zeitzone der Laufzeitumgebung. `en-CA` liefert die ISO-Reihenfolge und
 * ist damit gleichzeitig sortierbar.
 */
function berlinDay(millis: number): string {
  return BERLIN_DAY_FORMAT.format(new Date(millis));
}

/** `03.08.` */
function formatDay(millis: number): string {
  const [, month, day] = berlinDay(millis).split('-');
  return `${day}.${month}.`;
}

/** `03.08.2026` */
function formatDayWithYear(millis: number): string {
  const [year, month, day] = berlinDay(millis).split('-');
  return `${day}.${month}.${year}`;
}

/**
 * `03.08.–07.08.2026` — das Jahr steht nur am Ende, bleibt aber eindeutig.
 *
 * Exportiert, weil die Eltern-Ansicht ihre gebuchten Wochen mit genau diesem
 * Datumsbereich beschriftet (#1017) und dabei dieselbe Europe/Berlin-Rechnung
 * braucht wie der Export.
 */
export function formatVacationWeekRange(week: ExportWeek): string {
  return `${formatDay(week.startMillis)}–${formatDayWithYear(week.endMillis)}`;
}

/** `03.08.–07.08.` */
function formatShortRange(week: ExportWeek): string {
  return `${formatDay(week.startMillis)}–${formatDay(week.endMillis)}`;
}

/**
 * Baut die nummerierte Wochenliste einer Ferienperiode.
 *
 * Grundlage sind die konfigurierten Wochen. Zusätzlich gehen die tatsächlich
 * gebuchten Wochen ein: wurde die Ferienkonfiguration nachträglich geändert,
 * fiele eine Buchung sonst stumm aus dem Export.
 */
export function buildNumberedVacationWeeks(
  availableWeeks: ExportWeek[],
  bookedWeeks: ExportWeek[] = []
): NumberedVacationWeek[] {
  const byDay = new Map<string, ExportWeek>();

  for (const week of [...availableWeeks, ...bookedWeeks]) {
    if (!isValidWeek(week)) {
      continue;
    }
    const day = berlinDay(week.startMillis);
    if (!byDay.has(day)) {
      byDay.set(day, week);
    }
  }

  return [...byDay.values()]
    .sort((a, b) => a.startMillis - b.startMillis)
    .map((week, index) => ({
      ...week,
      number: index + 1,
      label: `Woche ${index + 1} (${formatVacationWeekRange(week)})`,
      shortLabel: `Woche ${index + 1} (${formatShortRange(week)})`,
    }));
}

/** Sucht die nummerierte Woche zu einem Startzeitpunkt. */
function findVacationWeek(
  weeks: NumberedVacationWeek[],
  startMillis: number
): NumberedVacationWeek | undefined {
  if (!Number.isFinite(startMillis)) {
    return undefined;
  }
  const day = berlinDay(startMillis);
  return weeks.find((week) => berlinDay(week.startMillis) === day);
}

/** Ist die Woche mit diesem Startzeitpunkt gebucht? */
export function isWeekBooked(
  bookedWeeks: ExportWeek[],
  startMillis: number
): boolean {
  if (!Number.isFinite(startMillis)) {
    return false;
  }
  const day = berlinDay(startMillis);
  return bookedWeeks.some(
    (week) => isValidWeek(week) && berlinDay(week.startMillis) === day
  );
}

/**
 * Sammelzelle des Exports:
 * `Woche 1 (03.08.–07.08.2026), Woche 2 (10.08.–14.08.2026)`.
 */
export function formatBookedWeeks(
  bookedWeeks: ExportWeek[],
  weeks: NumberedVacationWeek[]
): string {
  return bookedWeeks
    .filter(isValidWeek)
    .sort((a, b) => a.startMillis - b.startMillis)
    .map(
      (week) => findVacationWeek(weeks, week.startMillis)?.label ?? formatVacationWeekRange(week)
    )
    .join(', ');
}

/** Beschriftung einer gebuchten Woche in der Tabellenzeile, ohne Jahr. */
export function formatWeekShortLabel(
  bookedWeek: ExportWeek,
  weeks: NumberedVacationWeek[]
): string {
  return (
    findVacationWeek(weeks, bookedWeek.startMillis)?.shortLabel ??
    formatShortRange(bookedWeek)
  );
}
